import { useId } from "react";
import { LuCircleAlert } from "react-icons/lu";

export function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="cr-error">
      <LuCircleAlert aria-hidden="true" />
      {message}
    </p>
  );
}

// Label, optional hint and error around one control. The render prop gets
// the ids it needs for aria-describedby / aria-invalid.
export function Field({ label, optional, hint, error, className = "", children }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={`cr-field ${className}`.trim()}>
      <label className="cr-label" htmlFor={id}>
        {label} {optional && <span className="cr-optional">(optional)</span>}
      </label>
      {hint && (
        <p id={hintId} className="cr-hint">
          {hint}
        </p>
      )}
      {children({ id, describedBy, invalid: Boolean(error) })}
      <FieldError id={errorId} message={error} />
    </div>
  );
}

// A radio or checkbox group drawn as large tiles.
export function ChoiceGroup({
  legend,
  hint,
  name,
  options,
  value,
  onChange,
  error,
  multiple = false,
  columns,
  className = "",
  legendClassName = "cr-label",
}) {
  const id = useId();
  const errorId = error ? `${id}-error` : undefined;
  const selected = (option) =>
    multiple ? (value || []).includes(option.value) : value === option.value;

  const toggle = (option) => {
    if (!multiple) return onChange(option.value);
    const current = value || [];
    return onChange(
      current.includes(option.value)
        ? current.filter((v) => v !== option.value)
        : [...current, option.value],
    );
  };

  return (
    <fieldset className={`cr-field ${className}`.trim()} aria-describedby={errorId}>
      <legend className={legendClassName}>{legend}</legend>
      {hint && <p className="cr-hint cr-choice-hint">{hint}</p>}
      <div
        className={`cr-choices ${columns === 2 ? "cr-choices--two" : ""}`}
        data-invalid={Boolean(error)}
      >
        {options.map((option) => (
          <label key={String(option.value)} className="cr-choice">
            <input
              type={multiple ? "checkbox" : "radio"}
              name={name}
              checked={selected(option)}
              aria-invalid={Boolean(error)}
              onChange={() => toggle(option)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      <FieldError id={errorId} message={error} />
    </fieldset>
  );
}
